import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import { AppDispatch } from "configureStore";
import { upload, isEmptyUploadError } from "services/upload-file.service";
import {
  addMedicalCheckFile,
  deleteMedicalCheckFile,
  MedicalCheck,
  reopenMedicalCheck,
} from "services/medical-check.service";
import {
  setModalxComponent,
  setModalxComponentProps,
  setIsModalxOpen,
} from "components/modalx/modalx.slice";
import { API_URL } from "../../constants";

function ConfirmDeleteButton({
  onConfirm,
  label,
  confirmLabel,
}: {
  onConfirm: () => void;
  label: string;
  confirmLabel: string;
}) {
  const [confirming, setConfirming] = useState(false);

  if (confirming) {
    return (
      <div className="flex items-center gap-1 max-md:gap-3">
        <span className="text-xs text-red-600 font-medium">{confirmLabel}</span>
        <button
          className="px-2 py-1 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 max-md:min-h-11 max-md:min-w-11 max-md:text-sm"
          onClick={() => {
            setConfirming(false);
            onConfirm();
          }}
        >
          ✓
        </button>
        <button
          className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs font-medium hover:bg-gray-300 max-md:min-h-11 max-md:min-w-11 max-md:text-sm"
          onClick={() => setConfirming(false)}
        >
          ✕
        </button>
      </div>
    );
  }

  return (
    <button
      className="px-3 py-1.5 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 text-sm font-medium max-md:min-h-11"
      onClick={() => setConfirming(true)}
    >
      {label}
    </button>
  );
}

function formatDate(ts?: number | null) {
  if (!ts) return "—";
  return new Date(ts).toLocaleDateString("lt-LT");
}

const PHOTO_EXTENSION = /\.(jpe?g|png)$/i;

/** The preview window is a PDF viewer; a photo of the certificate opens in the browser's own viewer. */
export function viewMedicalFile(
  companyId: string,
  fileId: string,
  fileName: string,
  openPdfPreview: (fileId: string) => void
) {
  if (PHOTO_EXTENSION.test(fileName)) {
    window.open(`${API_URL}/${companyId}/files/download/${fileId}?mode=view`, "_blank");
    return;
  }
  openPdfPreview(fileId);
}

function FileActions({
  companyId,
  fileId,
  fileName,
  onViewFile,
  onDelete,
  showDelete,
  t,
}: {
  companyId: string;
  fileId: string;
  fileName: string;
  onViewFile: (fileId: string) => void;
  onDelete?: () => void;
  showDelete?: boolean;
  t: (key: string) => string;
}) {
  return (
    <div className="flex items-center gap-2 flex-wrap max-md:gap-3">
      <button
        className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium max-md:min-h-11"
        onClick={() => viewMedicalFile(companyId, fileId, fileName, onViewFile)}
      >
        {t("view")}
      </button>
      <button
        className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium max-md:min-h-11"
        onClick={() =>
          window.open(`${API_URL}/${companyId}/files/download/${fileId}`, "_blank")
        }
      >
        {t("download")}
      </button>
      {showDelete && onDelete && (
        <ConfirmDeleteButton
          onConfirm={onDelete}
          label={t("delete")}
          confirmLabel={t("confirm_delete")}
        />
      )}
    </div>
  );
}

export interface MedicalCheckBlockProps {
  companyId: string;
  check: MedicalCheck;
  canApproveMedical?: boolean;
  canUpload?: boolean;
  medicalApprovers?: string[];
  onReload: () => void;
  onViewFile: (fileId: string) => void;
  showEmployeeName?: boolean;
  /** Read-only completed row (e.g. recently approved in My Work queue). */
  completedView?: boolean;
}

export function MedicalCheckBlock({
  companyId,
  check,
  canApproveMedical = false,
  canUpload = false,
  medicalApprovers = [],
  onReload,
  onViewFile,
  showEmployeeName = false,
  completedView = false,
}: MedicalCheckBlockProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [uploading, setUploading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const isPending = check.status === "pending";
  const isApproved = check.status === "approved";
  const files = check.files || [];
  const hasFiles = files.length > 0;

  const handleUpload = async (fileList: File[]) => {
    if (!canUpload || !isPending) return;
    setUploading(true);
    let successCount = 0;
    let failCount = 0;
    for (const file of fileList) {
      try {
        const { data: fileId } = await upload(file, companyId);
        await addMedicalCheckFile(companyId, check.id, fileId);
        successCount++;
      } catch (error) {
        // An empty file was already toasted by upload() — not counted again.
        if (!isEmptyUploadError(error)) failCount++;
      }
    }
    if (successCount > 0) {
      await Promise.resolve(onReload());
    }
    if (failCount > 0 && successCount > 0) {
      toast.error(t("upload_partial_error"));
    } else if (failCount > 0) {
      toast.error(t("upload_error"));
    }
    setUploading(false);
  };

  const handleDeleteFile = async (fileRowId: string) => {
    try {
      await deleteMedicalCheckFile(companyId, check.id, fileRowId);
      onReload();
    } catch {
      toast.error(t("error"));
    }
  };

  const openApprove = () => {
    dispatch(setModalxComponent("ApproveMedicalCheckModal"));
    dispatch(
      setModalxComponentProps({
        companyId,
        checkId: check.id,
        onApproved: onReload,
      })
    );
    dispatch(setIsModalxOpen(true));
  };

  const handleReopen = async () => {
    setActionLoading(true);
    try {
      await reopenMedicalCheck(companyId, check.id);
      toast.success(t("medical_check_reopened"));
      onReload();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || t("error"));
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div
      className={`bg-white rounded-lg border p-5 shadow-sm ${
        completedView ? "border-green-200 bg-green-50/30" : "border-gray-200"
      }`}
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <h2 className="text-lg font-semibold">{t("medical_check")}</h2>
          {isApproved ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              {t("medical_check_approved")}
            </span>
          ) : isPending ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              {t("medical_check_pending")}
            </span>
          ) : (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
              {check.status}
            </span>
          )}
        </div>

        {showEmployeeName && check.employee?.name && (
          <p className="text-sm text-gray-600">{check.employee.name}</p>
        )}

        {isApproved && (check.validFrom || check.validUntil) && (
          <p className="text-sm text-gray-500">
            {t("medical_validity")}: {formatDate(check.validFrom)} –{" "}
            {formatDate(check.validUntil)}
          </p>
        )}

        {isApproved && check.approvedAt && (
          <p className="text-sm text-gray-500">
            {t("approved_at")}: {formatDate(check.approvedAt)}
          </p>
        )}

        {isApproved && canApproveMedical && !completedView && (
          <div className="flex items-center gap-3 flex-wrap">
            <button
              className="px-3 py-1.5 text-amber-700 border border-amber-300 rounded-lg hover:bg-amber-50 text-sm font-medium disabled:opacity-50 max-md:min-h-11"
              onClick={handleReopen}
              disabled={actionLoading}
            >
              {t("medical_check_reopen")}
            </button>
          </div>
        )}

        {hasFiles && (
          <div className="flex flex-col gap-2">
            {files.map((mf) => {
              const fileObj = typeof mf.file === "object" ? mf.file : null;
              if (!fileObj) return null;
              return (
                <div key={mf.id} className="flex items-center gap-3 flex-wrap">
                  <span className="text-sm min-w-0 flex-1">
                    {fileObj.name || fileObj.title || t("medical_certificate")}
                  </span>
                  <FileActions
                    companyId={companyId}
                    fileId={fileObj.id}
                    fileName={fileObj.title || fileObj.name || ""}
                    onViewFile={onViewFile}
                    onDelete={() => handleDeleteFile(mf.id)}
                    showDelete={
                      isPending &&
                      canUpload &&
                      !check.validFrom &&
                      !check.validUntil
                    }
                    t={t}
                  />
                </div>
              );
            })}
          </div>
        )}

        {isPending && canUpload && (
          <div>
            <label className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium cursor-pointer inline-block max-md:inline-flex max-md:min-h-11 max-md:items-center">
              {uploading ? t("uploading") : t("upload_medical_files")}
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                multiple
                className="hidden"
                disabled={uploading}
                onChange={(e) => {
                  if (e.target.files) {
                    handleUpload(Array.from(e.target.files));
                  }
                  e.target.value = "";
                }}
              />
            </label>
          </div>
        )}

        {isPending && (
          <div className="flex items-center gap-3 mt-2">
            {hasFiles && canApproveMedical && (
              <button
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50 max-md:min-h-11"
                onClick={openApprove}
                disabled={actionLoading}
              >
                {t("approve_medical")}
              </button>
            )}
            {hasFiles && !canApproveMedical && (
              <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg text-sm">
                <p className="font-medium">{t("medical_approve_not_authorized")}</p>
                {medicalApprovers.length > 0 && (
                  <p className="mt-1">
                    {t("can_approve")}: {medicalApprovers.join(", ")}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default MedicalCheckBlock;
